
import Vue from 'vue'
import Component from 'vue-class-component'
import {Prop, Watch} from 'vue-property-decorator'
import {
  DesignContext,
  DraggingContext,
  DragInfo,
  Interact,
  LayoutConfig,
  Part,
  Parts,
  Units,
  WebpartMoveLayerOptions,
} from '../types'
import {moveParts} from './helper'
import PartContainer from './PartContainer.vue'
import LayoutTile from './LayoutTile.vue'

@Component<PartsDesignerLayout>({
  name: 'parts-designer-layout',
  components: {PartContainer, LayoutTile},
})
export default class PartsDesignerLayout extends Vue {
  //
  // PROPS
  //

  @Prop({type: String}) name!: string
  @Prop({type: Object}) layout!: LayoutConfig
  // part list의 순서가 변경되기 때문에 value로 받아서 v-model 구현
  @Prop({type: Array}) value!: Parts
  @Prop({type: Object}) dragging!: DraggingContext
  @Prop({type: Object}) context!: DesignContext

  //
  // LOCAL STATES
  //

  placeholder = {
    interaction: 'resize',
    /**
     * 파트를 디자이너 레이아웃 위에서 Dragging할 때 예상 배치 영역 표시 여부
     */
    hidden: true,
    /**
     * 파트 배치 불가 상태 여부
     * 파트가 이미 존재할 경우, 해당 위치에 배치 불가
     */
    invalid: false,
    unit: 'px',
    /**
     * Position Top
     */
    top: 0,
    /**
     * Position Left
     */
    left: 0,
    width: 1,
    height: 1,
  }

  units: Units = {
    height: 0,
    width: 0,
  }

  /**
   * 디자이너 레이아웃에 배치된 파트에 대한 인터렉티브
   */
  interact: Interact = Object.create(null)

  $refs!: {
    layout: HTMLElement
  }

  //
  // COMPUTED
  //

  get placeholderStyle() {
    const {part: draggingPart} = this.dragging
    const {part: interactPart} = this.interact
    let colcnt = 1,
        rowcnt = 1
    if (this.interact.dragging) {
      colcnt = draggingPart.fixedColcnt
      rowcnt = draggingPart.fixedRowcnt
    }
    const {top: t, left: l, width, height, unit} = this.placeholder
    const style = {
      top: t + unit,
      left: l + unit,
      width: width * colcnt + unit,
      height: height * rowcnt + unit,
    }
    // console.log(
    //     'placeholderStyle',
    //     draggingPart,
    //     width,
    //     colcnt,
    //     height,
    //     rowcnt,
    //     style
    // )
    return style
  }

  get interactClass() {
    if (this.placeholder.hidden) return ''
    return 'design-board interact ' + this.placeholder.interaction
  }

  get parts() {
    return this.value.filter((x) => !x.available)
  }

  //
  // WATCHES
  //

  @Watch('layout.cols')
  layoutColsChanged() {
    this.calc()
  }

  @Watch('layout.rows')
  layoutRowsChanged() {
    this.calc()
    this.layout.designWidth
    this.layout.rowHeight
  }

  @Watch('layout.designWidth')
  layoutDesignWidthChanged() {
    this.calc()
  }

  @Watch('layout.rowHeight')
  layoutRowHeightChanged() {
    this.calc()
  }

  @Watch('dragging.active')
  draggingActiveChanged(dragging: boolean) {
    // console.log(this.name, 'dragging.active', dragging)
    if (dragging) this.dragStart()
    else this.dragEnd()
  }

  //
  // LIFECYCLE
  //

  created() {
    this.calc()

    // this is internal and should not be reactive
    this.interact = {
      part: Object.create(null),
      handler: Object.create(null),
      x0: 0,
      y0: 0,
      left: 0,
      top: 0,
      width: 0,
      height: 0,
      xu: 0,
      yu: 0,
      grid: null,
      dragging: false,
    }
  }

  //
  // METHODS
  //

  calc() {
    const {designWidth, cols, rowHeight} = this.layout
    this.units.width = designWidth / cols
    this.units.height = rowHeight // 150// 100 / this.layout.rows
  }

  //----------------------------------------------------------------------
  // Layer

  /**
   * 디자이너 레이아웃에서 파트의 레이어 위치를 이동합니다.
   * @param {WebpartMoveLayerOptions} options 레이어 이동 액션 옵션
   */
  handleLayerMoveAction({action, part}: WebpartMoveLayerOptions): void {
    const parts = this.value
    const idx = this.value.indexOf(part)
    var a: Part = Object.create(null)
    var to = -1
    switch (action) {
      case 'front':
        to = parts.length - 1
        break
      case 'forward':
        to = idx + 1
        // 디자이너 레이아웃에 있는 파트만 조회
        while (parts[to]?.available == true) ++to
        break
      case 'back':
        to = 0
        break
      case 'backward':
        to = idx - 1
        // 디자이너 레이아웃에 있는 파트만 조회
        while (parts[to]?.available == true) --to
        break
    }
    if (idx >= 0 && idx != to && to < parts.length && to >= 0) {
      const newparts = moveParts(parts, idx, to)
      this.$emit('input', newparts)
    }
  }

  //----------------------------------------------------------------------
  // resizing
  resizeStart(e: DragInfo) {
    this.interactStart(e, 'resize', this.resizeMove)
  }

  resizeMove(e: MouseEvent) {
    const {clientX, clientY} = e
    console.log('resizeMove', clientX, clientY)
    this.placeholder.width = this.interact.width + clientX - this.interact.x0
    this.placeholder.height = this.interact.height + clientY - this.interact.y0

    const {colidx, rowidx, isFixedSize, fixedColcnt, fixedRowcnt} =
        this.interact.part
    let colcnt = 1,
        rowcnt = 1
    if (isFixedSize) {
      colcnt = fixedColcnt
      rowcnt = fixedRowcnt
    }

    this.maybeUpdatePart(
        colidx,
        rowidx,
        Math.max(colcnt, Math.round(this.placeholder.width / this.interact.xu)),
        Math.max(rowcnt, Math.round(this.placeholder.height / this.interact.yu))
    )
  }

  //----------------------------------------------------------------------
  // positioning a part on the layout
  positionStart(e: DragInfo) {
    this.interactStart(e, 'drag', this.positionMove)
  }

  positionMove(e: MouseEvent) {
    this.placeholder.left =
        this.interact.left + e.clientX - this.interact.x0
    this.placeholder.top = this.interact.top + e.clientY - this.interact.y0
    this.maybeUpdatePart(
        Math.round(this.placeholder.left / this.interact.xu),
        Math.round(this.placeholder.top / this.interact.yu),
        this.interact.part.colcnt,
        this.interact.part.rowcnt
    )
  }

  //----------------------------------------------------------------------
  // drag and drop a chacheli from the chooser
  dragStart() {
    this.interact.dragging = false
    this.$el.addEventListener('mouseenter', this.dragEnter, false)
    this.$el.addEventListener('mouseleave', this.dragLeave, false)
    this.$el.addEventListener('mousemove', this.dragOver, false)
  }

  dragEnter(e: Event) {
    if (!this.dragging.active) return
    this.interact = {
      ...this.interact,
      dragging: true,
      x0: -1,
      y0: -1,
    }
    this.placeholder = {
      ...this.placeholder,
      unit: 'px',
      hidden: false,
      interaction: 'dnd',
      invalid: false,
      width: this.units.width,
      height: this.units.height,
    }
    // this.placeholder.w = this.dragging.part.fixedColcnt * this.units.h
    // this.placeholder.h = this.dragging.part.fixedRowcnt * this.units.v

    this.createValidationGrid(null)
    this.calcInteractUnits()

    this.dragOver(e)
  }

  dragOver(e: any) {
    let bounds = this.$el.getBoundingClientRect()
    let layout = this.$refs.layout
    const x = e.clientX
    const y =
        e.clientY +
        // scrollTop을 더해서 스크롤 영역에서의 위치를 계산
        layout.scrollTop
    let colidx = Math.floor((x - bounds.left) / this.interact.xu)
    let rowidx = Math.floor((y - bounds.top) / this.interact.yu)
    if (colidx == this.interact.x0 && rowidx == this.interact.y0) return
    const colcnt = this.dragging.part.fixedColcnt
    const rowcnt = this.dragging.part.fixedRowcnt

    this.interact.x0 = colidx
    this.interact.y0 = rowidx
    this.placeholder.left = colidx * this.units.width
    this.placeholder.top = rowidx * this.units.height

    if (this.dragging.part.type === 'shape') {
      // Shape는 파트와 겹쳐질 수 있음
      this.placeholder.invalid = false
    } else {
      // 파트끼리는 겹쳐지지 않도록
      this.placeholder.invalid = !this.validatePart(colidx, rowidx, colcnt, rowcnt)
    }
  }

  dragLeave() {
    this.draggingCleanup()
  }

  dragEnd() {
    this.$el.removeEventListener('mouseenter', this.dragEnter, false)
    this.$el.removeEventListener('mouseleave', this.dragLeave, false)
    this.$el.removeEventListener('mousemove', this.dragOver, false)
    this.interactCleanup()

    if (
        !this.interact.dragging ||
        this.dragging.cancelled ||
        this.placeholder.invalid
    ) {
      console.log('dragEnd', !this.interact.dragging, this.dragging.cancelled, this.placeholder.invalid)
      return
    }

    let part = this.dragging.part //this.value.filter((x) => x.id === this.dragging.id)[0]
    if (part && part.available) {
      part.available = false
      part.colidx = this.interact.x0
      part.rowidx = this.interact.y0
      part.colcnt = part.fixedColcnt
      part.rowcnt = part.fixedRowcnt
      // 파트를 디자이너에 추가 시 선택 상태로 만듬
      this.context.selected = part.webpartId
    }
    this.draggingCleanup()
  }

  draggingCleanup() {
    this.placeholder.hidden = true
    this.interact.dragging = false
  }

  //----------------------------------------------------------------------
  // code shared between resize/position
  interactStart(e: DragInfo, type: string, handler: (e: MouseEvent) => void) {
    const part = e.part
    this.placeholder.unit = 'px'
    this.placeholder.hidden = false
    this.placeholder.invalid = false
    this.placeholder.interaction = type
    this.placeholder.top = this.interact.top = e.initialPos.top
    this.placeholder.left = this.interact.left = e.initialPos.left
    this.placeholder.width = this.interact.width = e.initialPos.width
    this.placeholder.height = this.interact.height = e.initialPos.height
    this.interact.x0 = e.event.clientX
    this.interact.y0 = e.event.clientY
    this.interact.part = part
    this.interact.handler = handler

    this.calcInteractUnits()
    this.createValidationGrid(part)

    document.documentElement.addEventListener(
        'mousemove', this.interact.handler, false)
    document.documentElement.addEventListener(
        'mouseup', this.interactStop, false)
  }

  interactStop() {
    document.documentElement.removeEventListener(
        'mousemove', this.interact.handler, false)
    document.documentElement.removeEventListener(
        'mouseup', this.interactStop, false)
    this.interactCleanup()
  }

  //----------------------------------------------------------------------
  // helpers, etc.
  calcInteractUnits() {
    //@ts-ignore
    let cs = document.defaultView.getComputedStyle(this.$el)
    // console.log('calcInteractUnits', this.$el, cs.width, cs.height)
    this.interact.xu = this.units.width // Math.floor(parseInt(cs.width) / this.layout.cols)
    this.interact.yu = this.units.height // Math.floor(parseInt(cs.height) / this.layout.rows)
  }

  /**
   * 현재 파트의 배치 상태를 보고 인터렉트를 위한 그리드를 다시 생성한다.
   * @param ref
   */
  createValidationGrid(ref: Part | null) {
    let rows = this.layout.rows
    let cols = this.layout.cols

    this.interact.grid = new Array(rows)
    for (let r = 0; r < rows; r++) {
      let a = (this.interact.grid[r] = new Array(cols))
      for (let c = 0; c < cols; c++) a[c] = 1
    }

    // Shapes를 제외한 나머지 파트들의 레이아웃 배치도 작성
    // Shapes는 충돌을 감지하지 않음.
    const parts = this.value.filter((x) => x.type === 'part')
    for (let i = 0; i < parts.length; i++) {
      let part = parts[i]
      if (part === ref || part.available) continue
      for (let j = 0; j < part.rowcnt; j++) {
        for (let k = 0; k < part.colcnt; k++) {
          this.interact.grid[part.rowidx + j][part.colidx + k] = 0
        }
      }
    }
  }

  /**
   * 디자인 레이아웃의 특정 위치가 빈 공간인지 확인하여 Drop 하거나 Resize 가능한지 확인
   * @param {number} colidx column position x
   * @param {number} rowidx row position y
   * @param {number} colcnt column count
   * @param {number} rowcnt row count
   * @returns {boolean} 해당 공간에 파트가 위치할 수 있는지 여부
   */
  validatePart(
      colidx: number,
      rowidx: number,
      colcnt: number,
      rowcnt: number
  ): boolean {
    // rows
    for (let j = 0; j < rowcnt; j++) {
      // columns
      for (let k = 0; k < colcnt; k++) {
        let rowStatus = this.interact.grid![rowidx + j]
        if (
            !rowStatus ||
            // 위치의 값이 0인 경우, 해당 위치에 파트를 배치할 수 없음.
            !rowStatus[colidx + k]
        ) {
          // 해당 위치에 놓을 수 없음
          return false
        }
      }
    }
    // 빈 공간이어서 이 위치에 배치 허용
    return true
  }

  /**
   * 디자인 레이아웃의 특정 위치가 빈 공간인지 확인하여 파트의 위치 및 크기를 미리 설정
   * @param {number} colidx column position index
   * @param {number} rowidx row position index
   * @param {number} colcnt column count
   * @param {number} rowcnt row count
   */
  maybeUpdatePart(
      colidx: number,
      rowidx: number,
      colcnt: number,
      rowcnt: number
  ): void {
    let part = this.interact.part
    if (part.colidx === colidx && part.rowidx === rowidx &&
        part.colcnt === colcnt && part.rowcnt === rowcnt) {
      this.placeholder.invalid = false
      return
    }

    let valid = this.validatePart(colidx, rowidx, colcnt, rowcnt)
    if (part.type === 'shape' || valid) {
      part.colidx = colidx
      part.rowidx = rowidx
      part.colcnt = colcnt
      part.rowcnt = rowcnt
      this.placeholder.invalid = false
    } else {
      this.placeholder.invalid = true
    }
  }

  interactCleanup() {
    this.placeholder.hidden = true
    // this.interact.part = Object.create(null)
    this.interact.grid = null
  }

  minColumns() {
    let min = 1
    for (let i = 0; i < this.value.length; i++) {
      let c = this.value[i]
      if (!c.available) min = Math.max(min, c.colidx + c.colcnt)
    }
    return min
  }

  minRows() {
    let min = 1
    for (let i = 0; i < this.value.length; i++) {
      let c = this.value[i]
      if (!c.available) min = Math.max(min, c.rowidx + c.rowcnt)
    }
    return min
  }
}
